import React, { useState } from "react"
import Select, { components, OptionProps, Props, StylesConfig } from "react-select"

import { useMediaQuery } from "@hooks/useMediaQuery"

interface InputOptionProps extends OptionProps {
  getStyles: (key: string, data: any) => any
  Icon: React.FC
}

const InputOption = ({
  getStyles,
  Icon,
  isDisabled,
  isFocused,
  isSelected,
  children,
  innerProps,
  ...rest
}: InputOptionProps) => {
  const [isActive, setIsActive] = useState(false)
  const onMouseDown = () => setIsActive(true)
  const onMouseUp = () => setIsActive(false)
  const onMouseLeave = () => setIsActive(false)

  // styles
  let bg = "transparent"
  if (isFocused) bg = "#eee"
  if (isActive) bg = "#B2D4FF"

  const style = {
    alignItems: "center",
    backgroundColor: bg,
    color: "inherit",
    display: "flex ",
  }

  // prop assignment
  const props = {
    ...innerProps,
    onMouseDown,
    onMouseUp,
    onMouseLeave,
    style,
  }

  return (
    <components.Option
      {...rest}
      isDisabled={isDisabled}
      isFocused={isFocused}
      isSelected={isSelected}
      getStyles={getStyles}
      innerProps={props}
    >
      <input
        type={"checkbox"}
        className={"mr-1 rounded"}
        checked={isSelected}
        onChange={() => {}}
      />
      {children}
    </components.Option>
  )
}

interface MonoSelectWithCheckBoxProps extends Props {
  options: any
  value: any
  onChange: any
  hasError?: boolean
  allowSelectAll?: boolean
  size?: "xs" | "sm" | "md"
  showDropdownIcon?: boolean
  hideClearIcon?: boolean
  showSelectedOptions?: boolean
  selectedCountSuffixLabel?: string
  width?: boolean
}

const MonoSelectWithCheckBox = ({
  options,
  value,
  onChange,
  hasError = false,
  size = "sm",
  showDropdownIcon = true,
  hideClearIcon = false,
  showSelectedOptions = true,
  selectedCountSuffixLabel,
  width = false,
  ...props
}: MonoSelectWithCheckBoxProps) => {
  const isMobile = useMediaQuery(768)
  const colorStyles: StylesConfig = {
    container: (baseStyles) => ({
      ...baseStyles,
      width: width ? "" : "100%",
    }),
    control: (baseStyles, state) => ({
      ...baseStyles,
      background: "#FFFFFF",
      minHeight: size === "xs" ? "38px" : size === "sm" ? "40px" : "52px",
      borderColor: hasError ? "#DC3838" : state.isFocused ? "#2F69FE" : "#E7E7E8",
      "&:hover": {
        borderColor: hasError ? "#DC3838" : "#89B8FF",
      },
    }),
    dropdownIndicator: (baseStyles) => ({
      ...baseStyles,
      display: showDropdownIcon ? "block" : "none",
      padding: 0,
      fontSize: 0,
      paddingRight: size === "md" ? "16px" : "12px",
    }),
    indicatorSeparator: () => ({
      display: "none",
    }),
    valueContainer: (baseStyles) => ({
      ...baseStyles,
      paddingLeft: "14px",
      paddingRight: 0,
    }),
    singleValue: (baseStyles) => ({
      ...baseStyles,
      fontWeight: 400,
      fontSize: "14px",
      lineHeight: "120%",
      color: "#26272E",
    }),
    placeholder: (baseStyles) => ({
      ...baseStyles,
      fontWeight: 400,
      fontSize: "14px",
      lineHeight: "120%",
      color: "#898A93",
    }),
    menuPortal: (baseStyles) => ({
      ...baseStyles,
      zIndex: 10,
    }),
    menu: (baseStyles) => ({
      ...baseStyles,
      marginTop: "2px",
      padding: "16px 8px 8px 8px",
    }),
    input: (baseStyles) => ({
      ...baseStyles,
      boxShadow: "none",
      input: {
        boxShadow: "none !important",
      },
    }),
    option: (baseStyles, state) => ({
      ...baseStyles,
      padding: "8px",
      fontWeight: 400,
      fontSize: "14px",
      lineHeight: "120%",
      background: state.isSelected ? "#2F69FE" : "#FFFFFF",
      "&:hover": {
        background: state.isSelected ? "#2F69FE" : "#F2F6FF",
      },
      textTransform: "capitalize",
      cursor: "pointer",
    }),
    menuList: (base) => ({
      ...base,
      "::-webkit-scrollbar": {
        width: "3px",
        borderRadius: "6px",
      },
      "::-webkit-scrollbar-track": {
        background: "#E7E7E8",
      },
      "::-webkit-scrollbar-thumb": {
        background: "#CCCDD0",
        borderRadius: "6px",
      },
      "::-webkit-scrollbar-thumb:hover": {
        background: "#555",
      },
    }),
    clearIndicator: (baseStyles) => ({
      ...baseStyles,
      display: hideClearIcon ? "none" : "flex",
    }),
  }

  const CustomMultiValueContainer = ({ children }: any) => {
    return showSelectedOptions ? (
      <div className={"flex flex-row items-center rounded bg-neutral-200 mr-1"}>{children}</div>
    ) : null
  }
  const CustomValueContainer = (customValueProps: any) => {
    if (customValueProps?.selectProps?.value) {
      const uniqueArray = [
        ...new Map(customValueProps?.selectProps?.value.map((item) => [item.value, item])).values(),
      ]
      const filteredOptions = uniqueArray
        ? uniqueArray.filter((option) => option.value !== "all")
        : []

      if (uniqueArray?.length - 1 === customValueProps?.index)
        return (
          <div className={"paragraph14Regular text-neutral-800"}>
            {filteredOptions?.length > 0 ? `${filteredOptions?.length} ` : ""}
            {selectedCountSuffixLabel}
          </div>
        )
      return null
    }
    return null
  }

  return (
    <Select
      options={options}
      onChange={onChange}
      value={value?.length === 1 && value[0].value === "all" ? options : value}
      styles={colorStyles}
      classNamePrefix={"mono-select"}
      closeMenuOnScroll={(e: Event) =>
        isMobile &&
        e?.target &&
        !(e?.target as HTMLElement)?.classList?.contains("mono-select__menu-list")
      }
      components={
        showSelectedOptions
          ? {
              Option: InputOption as any,
              MultiValueContainer: (props) => <CustomMultiValueContainer {...props} />,
              // MultiValue: props => <CustomValueContainer {...props} />
            }
          : {
              Option: InputOption as any,
              MultiValueContainer: (props) => <CustomMultiValueContainer {...props} />,
              MultiValue: (props) => <CustomValueContainer {...props} />,
            }
      }
      {...props}
    />
  )
}

export default MonoSelectWithCheckBox
